/**
 * COS 上传缓存辅助工具
 * 用于管理 COS 上传缓存的查找、保存和清除
 */

var CacheService = require('../aws/services/cache-service');
var logger = require('../logger');
/**
 * 计算文件 MD5（非阻塞）
 * @param {File} file - 文件对象
 * @returns {Promise<string|null>} MD5 值或 null
 */
async function calculateFileMD5(file) {
    try {
        const { default: MD5Calculator } = await import('../md5/md5-calculator.js');
        const md5 = await MD5Calculator.calculateFileHash(file);
        logger.log('[umu-upload cos cache] MD5 calculated:', md5);
        return md5;
    } catch (error) {
        logger.log('[umu-upload cos cache] MD5 calculation failed:', error.message || error);
        return null; // 失败不阻塞
    }
}

/**
 * 从缓存查找 uploadInfo
 * @param {string} teacherId - 教师ID
 * @param {string} fileMD5 - 文件MD5
 * @returns {Promise<Object|null>} uploadInfo 或 null
 */
async function findCachedBucketInfo(teacherId, fileMD5) {
    if (!teacherId || !fileMD5) {
        logger.log('[umu-upload cos cache] findCachedBucketInfo: Missing parameters', { teacherId, fileMD5 });
        return Promise.resolve(null);
    }
    logger.log('[umu-upload cos cache] findCachedBucketInfo: Searching for cache', { teacherId, fileMD5 });

    var cacheService = new CacheService();
    return cacheService
        .findResumeRecord(teacherId, fileMD5)
        .then(function (record) {
            logger.log('[umu-upload cos cache] findResumeRecord result:', record);
            if (record && record.uploadInfo && record.uploadInfo.bucket_info) {
                logger.log('[umu-upload cos cache] Found cached COS bucket_info');
                return record.uploadInfo;
            }
            return null;
        })
        .catch(function (error) {
            logger.log('[umu-upload cos cache] Cache lookup failed:', error.message || error);
            return null;
        });
}

/**
 * 保存 bucket_info 到缓存
 * @param {string} teacherId - 教师ID
 * @param {string} fileMD5 - 文件MD5
 * @param {Object} bucketInfoData - 完整的 API 返回数据
 * @returns {Promise<void>}
 */
async function saveBucketInfoToCache(teacherId, fileMD5, bucketInfoData) {
    if (!teacherId || !fileMD5 || !bucketInfoData) {
        logger.log('[umu-upload cos cache] saveBucketInfoToCache: Missing parameters', {
            teacherId,
            fileMD5,
            hasBucketInfoData: !!bucketInfoData,
        });
        return Promise.resolve();
    }
    logger.log('[umu-upload cos cache] saveBucketInfoToCache: Saving cache', {
        teacherId,
        fileMD5,
        teacherIdType: typeof teacherId,
        fileMD5Type: typeof fileMD5,
        bucketInfoData: bucketInfoData,
    });
    var cacheService = new CacheService();
    var cacheRecord = {
        uploadId: 'cos_cache_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11),
        fileMD5: fileMD5,
        teacherId: teacherId,
        uploadInfo: {
            bucket_info: bucketInfoData.bucket_info,
            object_name: bucketInfoData.object_name,
            resource_id: bucketInfoData.resource_id,
        },
        createdAt: Date.now(),
    };
    try {
        const result = await cacheService.saveUploadRecord(cacheRecord);
        logger.log('[umu-upload cos cache] Bucket info saved to cache', result);
        return result;
    } catch (error) {
        logger.errorAndLog('[umu-upload cos cache] Failed to save cache:', error.message || error);
        return false;
    }
}

/**
 * 清除缓存
 * @param {string} teacherId - 教师ID
 * @param {string} fileMD5 - 文件MD5
 * @returns {Promise<boolean>}
 */
async function clearBucketInfoCache(teacherId, fileMD5) {
    if (!teacherId || !fileMD5) {
        return false;
    }
    var cacheService = new CacheService();
    try {
        const result = await cacheService.deleteUploadRecordByTeacherIdAndFileMD5(teacherId, fileMD5);
        logger.log('[umu-upload cos cache] Cache cleared', result);
        return result;
    } catch (error) {
        logger.errorAndLog('[umu-upload cos cache] Failed to clear cache:', error.message || error);
        return false;
    }
}

module.exports = {
    calculateFileMD5: calculateFileMD5,
    findCachedBucketInfo: findCachedBucketInfo,
    saveBucketInfoToCache: saveBucketInfoToCache,
    clearBucketInfoCache: clearBucketInfoCache,
};
